import React, { useEffect, useMemo, useState } from "react";
import { Bell, CheckCircle2, Eye, Megaphone, Pencil, Plus, Save, Search, Send, Smartphone, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <section className={cx("bg-white border border-slate-200 rounded-2xl p-5", className)}>
    {children}
  </section>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const newCampaignId = () => `hero-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
const newPushCampaignId = () => `push-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyForm = {
  id: "",
  name: "",
  description: "",
  offerText: "",
  slug: "",
  mainProductId: "",
  productIds: [],
  createdAt: null,
};

const emptyPushForm = {
  id: "",
  title: "",
  message: "",
  heroTitle: "",
  heroText: "",
  buttonLabel: "Shop now",
  slug: "",
  productIds: [],
  createdAt: null,
};

const toUniqueProducts = (rows) => {
  const seen = new Set();
  const list = [];
  for (const row of rows || []) {
    const productId = String(row?.productId || "").trim();
    if (!productId || seen.has(productId)) continue;
    seen.add(productId);
    list.push({
      productId,
      name: String(row?.name || "Untitled product"),
      image:
        row?.images?.[0] ||
        row?.image ||
        "https://placehold.co/200x200/F1F5F9/0F172A?text=Cherrfy",
      price: row?.price || null,
      currency: row?.currency || "USD",
    });
  }
  return list;
};

const getPushLandingPath = (campaign) => {
  const slug = String(campaign?.slug || "").trim();
  return slug ? `/push-marketing/${slug}` : "/push-marketing/your-slug";
};

const formatPushCounts = (campaign) => {
  const counts = campaign?.lastPushResult?.counts || {};
  const sent = Number(counts.sent || 0);
  const failed = Number(counts.failed || 0);
  const total = Number(counts.totalTokens || 0);
  if (!campaign?.lastSentAt) return "Not sent yet";
  return `${sent}/${total} tokens sent${failed ? `, ${failed} failed` : ""}`;
};

export default function CampaignsDiscounts() {
  const [activePanel, setActivePanel] = useState("hero");
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingPushCampaigns, setLoadingPushCampaigns] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPush, setSavingPush] = useState(false);
  const [deletingCampaignId, setDeletingCampaignId] = useState("");
  const [sendingPushId, setSendingPushId] = useState("");
  const [deletingPushId, setDeletingPushId] = useState("");

  const [campaigns, setCampaigns] = useState([]);
  const [pushCampaigns, setPushCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState("");
  const [bestDealsCampaignId, setBestDealsCampaignId] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pushUpdatedAt, setPushUpdatedAt] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pushForm, setPushForm] = useState(emptyPushForm);
  const [productSearch, setProductSearch] = useState("");
  const [pushProductSearch, setPushProductSearch] = useState("");
  const [products, setProducts] = useState([]);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await API.get("/settings/hero-campaigns");
      setCampaigns(Array.isArray(res?.data?.campaigns) ? res.data.campaigns : []);
      setActiveCampaignId(String(res?.data?.activeCampaignId || ""));
      setBestDealsCampaignId(String(res?.data?.bestDealsCampaignId || ""));
      setUpdatedAt(res?.data?.updated_at || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load hero campaigns.");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const all = [];
      let page = 1;
      let hasMore = true;
      const limit = 100;
      const maxPages = 8;

      while (hasMore && page <= maxPages) {
        const res = await API.get("/product/admin/all", {
          params: { currency: "USD", priceCountry: "US", page, limit },
        });
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        all.push(...rows);
        hasMore = Boolean(res?.data?.pagination?.hasMore);
        page += 1;
      }

      setProducts(toUniqueProducts(all));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadPushCampaigns = async () => {
    setLoadingPushCampaigns(true);
    try {
      const res = await API.get("/push-marketing");
      setPushCampaigns(Array.isArray(res?.data?.campaigns) ? res.data.campaigns : []);
      setPushUpdatedAt(res?.data?.updated_at || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to load push marketing campaigns.");
    } finally {
      setLoadingPushCampaigns(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
    loadPushCampaigns();
    loadProducts();
  }, []);

  const persist = async (nextCampaigns, nextActiveCampaignId, nextBestDealsCampaignId) => {
    setSaving(true);
    try {
      const res = await API.put("/settings/hero-campaigns", {
        campaigns: nextCampaigns,
        activeCampaignId: nextActiveCampaignId || "",
        bestDealsCampaignId: nextBestDealsCampaignId || "",
      });
      const normalizedCampaigns = Array.isArray(res?.data?.campaigns) ? res.data.campaigns : [];
      setCampaigns(normalizedCampaigns);
      setActiveCampaignId(String(res?.data?.activeCampaignId || ""));
      setBestDealsCampaignId(String(res?.data?.bestDealsCampaignId || ""));
      setUpdatedAt(res?.data?.updated_at || null);
      toast.success("Hero campaigns updated.");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save hero campaigns.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSaveForm = async () => {
    const name = String(form?.name || "").trim();
    const description = String(form?.description || "").trim();
    const offerText = String(form?.offerText || "").trim();
    const productIds = Array.from(new Set((form?.productIds || []).map((id) => String(id || "").trim()).filter(Boolean)));
    const pageSlug = slugify(form?.slug || name || "");
    let mainProductId = String(form?.mainProductId || "").trim();
    if (!mainProductId || !productIds.includes(mainProductId)) {
      mainProductId = productIds[0] || "";
    }

    if (!name) {
      toast.error("Campaign name is required.");
      return;
    }
    if (!productIds.length) {
      toast.error("Select at least one product for hero campaign.");
      return;
    }
    if (!pageSlug) {
      toast.error("Campaign page slug is required.");
      return;
    }
    if (!mainProductId) {
      toast.error("Select one main product.");
      return;
    }

    const id = form.id || newCampaignId();
    const nextCampaign = {
      id,
      name,
      description,
      offerText,
      slug: pageSlug,
      mainProductId,
      productIds,
      createdAt: form.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const exists = campaigns.some((c) => String(c?.id) === id);
    const nextCampaigns = exists
      ? campaigns.map((c) => (String(c?.id) === id ? { ...c, ...nextCampaign } : c))
      : [nextCampaign, ...campaigns];
    const nextActive = activeCampaignId || id;
    const nextBestDeals = bestDealsCampaignId || nextActive || id;

    const ok = await persist(nextCampaigns, nextActive, nextBestDeals);
    if (ok) {
      setForm(emptyForm);
      setProductSearch("");
    }
  };

  const onDeleteCampaign = async (campaign) => {
    const id = String(campaign?.id || "");
    const identifier = String(campaign?.slug || campaign?.id || "").trim();
    if (!identifier) return;
    const ok = window.confirm("Delete this campaign?");
    if (!ok) return;

    setDeletingCampaignId(id || identifier);
    try {
      const res = await API.delete(`/settings/hero-campaigns/${encodeURIComponent(identifier)}`);
      const normalizedCampaigns = Array.isArray(res?.data?.campaigns) ? res.data.campaigns : [];
      setCampaigns(normalizedCampaigns);
      setActiveCampaignId(String(res?.data?.activeCampaignId || ""));
      setBestDealsCampaignId(String(res?.data?.bestDealsCampaignId || ""));
      setUpdatedAt(res?.data?.updated_at || null);
      if (String(form?.id || "") === id || String(form?.slug || "") === identifier) setForm(emptyForm);
      toast.success("Hero campaign deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to delete hero campaign.");
    } finally {
      setDeletingCampaignId("");
    }
  };

  const onEditCampaign = (campaign) => {
    setForm({
      id: String(campaign?.id || ""),
      name: String(campaign?.name || ""),
      description: String(campaign?.description || ""),
      offerText: String(campaign?.offerText || ""),
      slug: String(campaign?.slug || ""),
      mainProductId: String(campaign?.mainProductId || campaign?.productIds?.[0] || ""),
      productIds: Array.isArray(campaign?.productIds) ? campaign.productIds.map((id) => String(id)) : [],
      createdAt: campaign?.createdAt || null,
    });
  };

  const onActivateCampaign = async (campaignId) => {
    const id = String(campaignId || "");
    if (!id) return;
    await persist(campaigns, id, bestDealsCampaignId || id);
  };

  const onSetBestDealsCampaign = async (campaignId) => {
    const id = String(campaignId || "");
    if (!id) return;
    await persist(campaigns, activeCampaignId || id, id);
  };

  const onSavePushMarketing = async () => {
    const title = String(pushForm?.title || "").trim();
    const message = String(pushForm?.message || "").trim();
    const heroTitle = String(pushForm?.heroTitle || title || "").trim();
    const heroText = String(pushForm?.heroText || message || "").trim();
    const buttonLabel = String(pushForm?.buttonLabel || "Shop now").trim();
    const productIds = Array.from(new Set((pushForm?.productIds || []).map((id) => String(id || "").trim()).filter(Boolean)));
    const pageSlug = slugify(pushForm?.slug || title || "");

    if (!title) {
      toast.error("Push campaign title is required.");
      return;
    }
    if (!message) {
      toast.error("Push message is required.");
      return;
    }
    if (!productIds.length) {
      toast.error("Select at least one product for push marketing.");
      return;
    }
    if (!pageSlug) {
      toast.error("Push marketing page slug is required.");
      return;
    }

    const payload = {
      id: pushForm.id || newPushCampaignId(),
      title,
      message,
      heroTitle,
      heroText,
      buttonLabel,
      slug: pageSlug,
      productIds,
      createdAt: pushForm.createdAt || undefined,
    };

    setSavingPush(true);
    try {
      const res = pushForm.id
        ? await API.put(`/push-marketing/${encodeURIComponent(pushForm.id)}`, payload)
        : await API.post("/push-marketing", payload);
      setPushCampaigns(Array.isArray(res?.data?.campaigns) ? res.data.campaigns : []);
      setPushUpdatedAt(res?.data?.updated_at || null);
      setPushForm(emptyPushForm);
      setPushProductSearch("");
      toast.success("Push marketing campaign saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to save push marketing campaign.");
    } finally {
      setSavingPush(false);
    }
  };

  const onEditPushCampaign = (campaign) => {
    setActivePanel("push");
    setPushForm({
      id: String(campaign?.id || ""),
      title: String(campaign?.title || ""),
      message: String(campaign?.message || ""),
      heroTitle: String(campaign?.heroTitle || campaign?.title || ""),
      heroText: String(campaign?.heroText || campaign?.message || ""),
      buttonLabel: String(campaign?.buttonLabel || "Shop now"),
      slug: String(campaign?.slug || ""),
      productIds: Array.isArray(campaign?.productIds) ? campaign.productIds.map((id) => String(id)) : [],
      createdAt: campaign?.createdAt || null,
    });
  };

  const onDeletePushCampaign = async (campaign) => {
    const id = String(campaign?.id || "");
    const identifier = String(campaign?.slug || campaign?.id || "").trim();
    if (!identifier) return;
    const ok = window.confirm("Delete this push marketing campaign?");
    if (!ok) return;

    setDeletingPushId(id || identifier);
    try {
      const res = await API.delete(`/push-marketing/${encodeURIComponent(identifier)}`);
      setPushCampaigns(Array.isArray(res?.data?.campaigns) ? res.data.campaigns : []);
      setPushUpdatedAt(res?.data?.updated_at || null);
      if (String(pushForm?.id || "") === id || String(pushForm?.slug || "") === identifier) setPushForm(emptyPushForm);
      toast.success("Push marketing campaign deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to delete push marketing campaign.");
    } finally {
      setDeletingPushId("");
    }
  };

  const onSendPushCampaign = async (campaign) => {
    const id = String(campaign?.id || "");
    if (!id) return;
    const ok = window.confirm(`Send "${campaign?.title || "this push campaign"}" to all app users with push tokens?`);
    if (!ok) return;

    setSendingPushId(id);
    try {
      const res = await API.post(`/push-marketing/${encodeURIComponent(id)}/send`);
      setPushCampaigns(Array.isArray(res?.data?.campaigns) ? res.data.campaigns : []);
      setPushUpdatedAt(res?.data?.updated_at || null);
      toast.success(res?.data?.message || "Push marketing sent.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to send push marketing.");
    } finally {
      setSendingPushId("");
    }
  };

  const toggleProduct = (productId) => {
    const id = String(productId || "").trim();
    if (!id) return;
    setForm((prev) => {
      const list = Array.isArray(prev?.productIds) ? prev.productIds : [];
      if (list.includes(id)) {
        const nextList = list.filter((pid) => pid !== id);
        const nextMain =
          String(prev?.mainProductId || "") === id
            ? String(nextList?.[0] || "")
            : String(prev?.mainProductId || "");
        return { ...prev, productIds: nextList, mainProductId: nextMain };
      }
      const nextList = [...list, id];
      const nextMain = String(prev?.mainProductId || "") || id;
      return { ...prev, productIds: nextList, mainProductId: nextMain };
    });
  };

  const togglePushProduct = (productId) => {
    const id = String(productId || "").trim();
    if (!id) return;
    setPushForm((prev) => {
      const list = Array.isArray(prev?.productIds) ? prev.productIds : [];
      if (list.includes(id)) {
        return { ...prev, productIds: list.filter((pid) => pid !== id) };
      }
      return { ...prev, productIds: [...list, id] };
    });
  };

  const productNameById = useMemo(() => {
    const map = {};
    for (const product of products) {
      map[String(product?.productId || "")] = product?.name || "";
    }
    return map;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = String(productSearch || "").trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      String(p?.name || "").toLowerCase().includes(q) ||
      String(p?.productId || "").toLowerCase().includes(q)
    );
  }, [products, productSearch]);
  const selectedProducts = useMemo(() => {
    const selectedIds = new Set((form?.productIds || []).map((id) => String(id)));
    return products.filter((p) => selectedIds.has(String(p?.productId || "")));
  }, [products, form?.productIds]);

  const filteredPushProducts = useMemo(() => {
    const q = String(pushProductSearch || "").trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      String(p?.name || "").toLowerCase().includes(q) ||
      String(p?.productId || "").toLowerCase().includes(q)
    );
  }, [products, pushProductSearch]);

  const selectedPushProducts = useMemo(() => {
    const selectedIds = new Set((pushForm?.productIds || []).map((id) => String(id)));
    return products.filter((p) => selectedIds.has(String(p?.productId || "")));
  }, [products, pushForm?.productIds]);

  const isBusy = loadingCampaigns || loadingProducts;
  const isPushBusy = loadingPushCampaigns || loadingProducts;

  return (
    <AdminPageShell
      title="Campaigns & Discounts"
      description={
        activePanel === "push"
          ? "Create product landing pages for app push marketing and send them to Cherrfy app users."
          : "Create hero section campaigns and assign products that should appear on cherrfy.com homepage."
      }
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black text-slate-800">
            {activePanel === "push" ? <Bell className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
            {activePanel === "push" ? "Push Marketing" : "Hero Section Campaign"}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700">
            {activePanel === "push" ? <Smartphone className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
            {activePanel === "push" ? "App-ready route" : "Storefront homepage"}
          </span>
        </div>
      }
      actions={
        <SecondaryButton
          type="button"
          onClick={() => {
            if (activePanel === "push") {
              setPushForm(emptyPushForm);
              setPushProductSearch("");
              return;
            }
            setForm(emptyForm);
          }}
        >
          <Plus size={16} />
          {activePanel === "push" ? "New Push Campaign" : "New Campaign"}
        </SecondaryButton>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1">
        <button
          type="button"
          onClick={() => setActivePanel("hero")}
          className={cx(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition-colors",
            activePanel === "hero"
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-50"
          )}
        >
          <Megaphone size={16} />
          Hero Campaigns
        </button>
        <button
          type="button"
          onClick={() => setActivePanel("push")}
          className={cx(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition-colors",
            activePanel === "push"
              ? "bg-[#D2042D] text-white"
              : "text-slate-600 hover:bg-slate-50"
          )}
        >
          <Bell size={16} />
          Push Marketing
        </button>
      </div>

      {activePanel === "hero" ? (
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-6">
        <div className="xl:col-span-7">
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                  {form.id ? "Edit Hero Campaign" : "Create Hero Campaign"}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Choose products for homepage hero slider. First 5 items become hero slides.
                </p>
              </div>
              <PrimaryButton type="button" onClick={onSaveForm} disabled={saving || isBusy}>
                <Save size={16} />
                {saving ? "Saving..." : "Save Campaign"}
              </PrimaryButton>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Campaign Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className={FieldBase}
                  placeholder="e.g. Eid Hero Deals"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={cx(FieldBase, "min-h-[88px] resize-y")}
                  placeholder="Short internal note for this campaign."
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Offer Text
                  </label>
                  <input
                    value={form.offerText}
                    onChange={(e) => setForm((prev) => ({ ...prev, offerText: e.target.value }))}
                    className={FieldBase}
                    placeholder="e.g. Up to 45% OFF today"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Campaign Page Slug
                  </label>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                    className={FieldBase}
                    placeholder="e.g. eid-hero-deals"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Page URL: <span className="font-semibold text-slate-700">/campaign/{form.slug || "your-slug"}</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Select Products
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className={cx(FieldBase, "pl-10")}
                    placeholder="Search by product name or product ID..."
                  />
                </div>

                <div className="mt-3 max-h-[360px] overflow-auto rounded-2xl border border-slate-200">
                  {loadingProducts ? (
                    <div className="p-4 text-sm text-slate-600">Loading products...</div>
                  ) : filteredProducts.length ? (
                    <div className="divide-y divide-slate-100">
                      {filteredProducts.map((product) => {
                        const checked = form.productIds.includes(product.productId);
                        return (
                          <label
                            key={product.productId}
                            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleProduct(product.productId)}
                              className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
                            />
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-10 w-10 rounded-lg object-cover border border-slate-200"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-900">{product.name}</p>
                              <p className="truncate text-xs text-slate-500">{product.productId}</p>
                            </div>
                            <div className="text-xs font-semibold text-[#D2042D]">
                              {product.price ? `${product.currency} ${product.price}` : ""}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-sm text-slate-600">No products found.</div>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Selected: <span className="font-semibold text-slate-700">{form.productIds.length}</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Main Product
                </label>
                <select
                  value={form.mainProductId}
                  onChange={(e) => setForm((prev) => ({ ...prev, mainProductId: String(e.target.value || "") }))}
                  className={FieldBase}
                  disabled={!selectedProducts.length}
                >
                  {!selectedProducts.length ? <option value="">Select products first</option> : null}
                  {selectedProducts.map((p) => (
                    <option key={p.productId} value={p.productId}>
                      {p.name} ({p.productId})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  This product is shown as campaign card after hero section.
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="xl:col-span-5">
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">Hero Campaigns</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Active campaign is used on storefront hero section.
                </p>
              </div>
              {updatedAt ? (
                <span className="text-[11px] text-slate-500">{new Date(updatedAt).toLocaleString()}</span>
              ) : null}
            </div>

            <div className="mt-4 space-y-3">
              {loadingCampaigns ? (
                <div className="text-sm text-slate-600">Loading campaigns...</div>
              ) : campaigns.length ? (
                campaigns.map((campaign) => {
                  const id = String(campaign?.id || "");
                  const isActive = id === String(activeCampaignId || "");
                  const isBestDeals = id === String(bestDealsCampaignId || "");
                  return (
                    <div key={id} className="rounded-2xl border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-900">{campaign?.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-2">{campaign?.description || "No description"}</p>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {isActive ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700">
                              <CheckCircle2 size={12} />
                              Hero
                            </span>
                          ) : null}
                          {isBestDeals ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700">
                              <CheckCircle2 size={12} />
                              Best deals
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <p className="mt-2 text-xs text-slate-600">
                        Products: <span className="font-semibold">{Array.isArray(campaign?.productIds) ? campaign.productIds.length : 0}</span>
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        Main: <span className="font-semibold">{productNameById[campaign?.mainProductId] || campaign?.mainProductId || "N/A"}</span>
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        Page: <span className="font-semibold">/campaign/{campaign?.slug || campaign?.id}</span>
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(Array.isArray(campaign?.productIds) ? campaign.productIds : []).slice(0, 3).map((pid) => (
                          <span
                            key={`${id}-${pid}`}
                            className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600"
                            title={productNameById[pid] || pid}
                          >
                            {productNameById[pid] || pid}
                          </span>
                        ))}
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <SecondaryButton
                          type="button"
                          onClick={() => onActivateCampaign(id)}
                          className="px-3 py-1.5 text-xs"
                          disabled={saving || isBusy || isActive}
                        >
                          Activate
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          onClick={() => onEditCampaign(campaign)}
                          className="px-3 py-1.5 text-xs"
                          disabled={saving || isBusy}
                        >
                          <Pencil size={13} />
                          Edit
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          onClick={() => onSetBestDealsCampaign(id)}
                          className="px-3 py-1.5 text-xs"
                          disabled={saving || isBusy || isBestDeals}
                        >
                          Best deals
                        </SecondaryButton>
                        <button
                          type="button"
                          onClick={() => onDeleteCampaign(campaign)}
                          className="inline-flex items-center gap-1 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 transition-colors"
                          disabled={saving || deletingCampaignId === id}
                        >
                          <Trash2 size={13} />
                          {deletingCampaignId === id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  No hero campaigns yet.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-6">
          <div className="xl:col-span-7">
            <Card>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                    {pushForm.id ? "Edit Push Marketing" : "Create Push Marketing"}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Select products, create an app landing page, then send a push notification that opens it.
                  </p>
                </div>
                <PrimaryButton type="button" onClick={onSavePushMarketing} disabled={savingPush || isPushBusy}>
                  <Save size={16} />
                  {savingPush ? "Saving..." : "Save Push Campaign"}
                </PrimaryButton>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Push Title
                  </label>
                  <input
                    value={pushForm.title}
                    onChange={(e) => setPushForm((prev) => ({ ...prev, title: e.target.value }))}
                    className={FieldBase}
                    maxLength={100}
                    placeholder="e.g. New phone deals are live"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Push Message
                  </label>
                  <textarea
                    value={pushForm.message}
                    onChange={(e) => setPushForm((prev) => ({ ...prev, message: e.target.value }))}
                    className={cx(FieldBase, "min-h-[88px] resize-y")}
                    maxLength={240}
                    placeholder="Short text users will see in the notification."
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      Landing Heading
                    </label>
                    <input
                      value={pushForm.heroTitle}
                      onChange={(e) => setPushForm((prev) => ({ ...prev, heroTitle: e.target.value }))}
                      className={FieldBase}
                      maxLength={120}
                      placeholder="Defaults to push title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      App Landing Slug
                    </label>
                    <input
                      value={pushForm.slug}
                      onChange={(e) => setPushForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                      className={FieldBase}
                      placeholder="e.g. phone-deals"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      App route: <span className="font-semibold text-slate-700">/push-marketing/{pushForm.slug || slugify(pushForm.title) || "your-slug"}</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      Landing Intro
                    </label>
                    <textarea
                      value={pushForm.heroText}
                      onChange={(e) => setPushForm((prev) => ({ ...prev, heroText: e.target.value }))}
                      className={cx(FieldBase, "min-h-[76px] resize-y")}
                      maxLength={320}
                      placeholder="Defaults to push message"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      Landing Button Text
                    </label>
                    <input
                      value={pushForm.buttonLabel}
                      onChange={(e) => setPushForm((prev) => ({ ...prev, buttonLabel: e.target.value }))}
                      className={FieldBase}
                      maxLength={32}
                      placeholder="Shop now"
                    />
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                      Users who tap the push open the campaign page in the Cherrfy app, then tap any product to shop.
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Select Landing Products
                  </label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={pushProductSearch}
                      onChange={(e) => setPushProductSearch(e.target.value)}
                      className={cx(FieldBase, "pl-10")}
                      placeholder="Search by product name or product ID..."
                    />
                  </div>

                  <div className="mt-3 max-h-[360px] overflow-auto rounded-2xl border border-slate-200">
                    {loadingProducts ? (
                      <div className="p-4 text-sm text-slate-600">Loading products...</div>
                    ) : filteredPushProducts.length ? (
                      <div className="divide-y divide-slate-100">
                        {filteredPushProducts.map((product) => {
                          const checked = pushForm.productIds.includes(product.productId);
                          return (
                            <label
                              key={`push-${product.productId}`}
                              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePushProduct(product.productId)}
                                className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
                              />
                              <img
                                src={product.image}
                                alt={product.name}
                                className="h-10 w-10 rounded-lg object-cover border border-slate-200"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-slate-900">{product.name}</p>
                                <p className="truncate text-xs text-slate-500">{product.productId}</p>
                              </div>
                              <div className="text-xs font-semibold text-[#D2042D]">
                                {product.price ? `${product.currency} ${product.price}` : ""}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 text-sm text-slate-600">No products found.</div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Selected: <span className="font-semibold text-slate-700">{pushForm.productIds.length}</span>
                  </p>
                </div>

                {selectedPushProducts.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Landing Preview Products</p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {selectedPushProducts.slice(0, 6).map((product) => (
                        <div key={`preview-${product.productId}`} className="flex items-center gap-2 rounded-xl bg-white p-2">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-10 w-10 rounded-lg border border-slate-200 object-cover"
                          />
                          <div className="min-w-0">
                            <p className="truncate text-xs font-bold text-slate-900">{product.name}</p>
                            <p className="text-[11px] font-semibold text-[#D2042D]">
                              {product.price ? `${product.currency} ${product.price}` : "Price pending"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>
          </div>

          <div className="xl:col-span-5">
            <Card>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">Push Marketing</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Saved product landing pages for app push campaigns.
                  </p>
                </div>
                {pushUpdatedAt ? (
                  <span className="text-[11px] text-slate-500">{new Date(pushUpdatedAt).toLocaleString()}</span>
                ) : null}
              </div>

              <div className="mt-4 space-y-3">
                {loadingPushCampaigns ? (
                  <div className="text-sm text-slate-600">Loading push marketing campaigns...</div>
                ) : pushCampaigns.length ? (
                  pushCampaigns.map((campaign) => {
                    const id = String(campaign?.id || "");
                    const isSending = sendingPushId === id;
                    const isDeleting = deletingPushId === id;
                    return (
                      <div key={id} className="rounded-2xl border border-slate-200 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-900">{campaign?.title}</p>
                            <p className="text-xs text-slate-500 line-clamp-2">{campaign?.message || "No push message"}</p>
                          </div>
                          <span className={cx(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold",
                            campaign?.status === "sent"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-50 text-slate-700"
                          )}>
                            <CheckCircle2 size={12} />
                            {campaign?.status === "sent" ? "Sent" : "Draft"}
                          </span>
                        </div>

                        <div className="mt-3 space-y-1 text-xs text-slate-600">
                          <p className="flex items-center gap-1.5">
                            <Eye size={13} className="text-slate-400" />
                            Page: <span className="font-semibold text-slate-800">{getPushLandingPath(campaign)}</span>
                          </p>
                          <p>
                            Products: <span className="font-semibold">{Array.isArray(campaign?.productIds) ? campaign.productIds.length : 0}</span>
                          </p>
                          <p>
                            Last push: <span className="font-semibold">{campaign?.lastSentAt ? new Date(campaign.lastSentAt).toLocaleString() : "Not sent"}</span>
                          </p>
                          <p>
                            Delivery: <span className="font-semibold">{formatPushCounts(campaign)}</span>
                          </p>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-1">
                          {(Array.isArray(campaign?.productIds) ? campaign.productIds : []).slice(0, 4).map((pid) => (
                            <span
                              key={`${id}-${pid}`}
                              className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600"
                              title={productNameById[pid] || pid}
                            >
                              {productNameById[pid] || pid}
                            </span>
                          ))}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <PrimaryButton
                            type="button"
                            onClick={() => onSendPushCampaign(campaign)}
                            className="px-3 py-1.5 text-xs"
                            disabled={isSending || savingPush || isPushBusy}
                          >
                            <Send size={13} />
                            {isSending ? "Sending..." : "Send Push"}
                          </PrimaryButton>
                          <SecondaryButton
                            type="button"
                            onClick={() => onEditPushCampaign(campaign)}
                            className="px-3 py-1.5 text-xs"
                            disabled={isSending || savingPush || isPushBusy}
                          >
                            <Pencil size={13} />
                            Edit
                          </SecondaryButton>
                          <button
                            type="button"
                            onClick={() => onDeletePushCampaign(campaign)}
                            className="inline-flex items-center gap-1 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 transition-colors"
                            disabled={isDeleting || isSending || savingPush}
                          >
                            <Trash2 size={13} />
                            {isDeleting ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    No push marketing campaigns yet.
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
}
